/* SSi – Sales / landing page sections for schools.
 *
 * Sister page to TeachersOnline.jsx (for-teachers-online.html). Same
 * visual vocabulary – Arsenal + Open Sans, brandRed, cream/white/red
 * section rhythm, the shared Header + Footer, the <ssi-lesson-player>
 * Sample widget, and the accordion FAQ.
 *
 * Audience: BOTH school decision-makers (heads, MFL / Welsh-medium
 * leads, MATs) AND individual teachers setting up for one class. The
 * page deliberately welcomes a single teacher as well as a whole
 * school – we expect to reach teachers first.
 *
 * CTAs route to the app account-setup flow, NOT the consumer checkout.
 * One constant per destination so Code can point them at the live
 * endpoint in one place.
 *
 * Adapted from the static design handoff (15 Jun 2026) into the SPA's
 * conventions: ESM imports, react-router <Link> for internal routes,
 * the shared ensureLessonPlayer loader, and /assets/ paths. The two
 * route files (routes/SchoolsWelsh.jsx, routes/SchoolsLanguages.jsx)
 * supply the copy and render the Header/Footer + sections.
 */
import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ensureLessonPlayer } from '../lessonPlayerLoader.js';

/* ---- CTA destinations -------------------------------------------------
 * Each page supplies its own app account-setup URL via t.scSetupUrl:
 *   SchoolsWelsh (Welsh/Irish) -> /schools1
 *   SchoolsLanguages (majority) -> /schools2
 * SCHOOL_SETUP_URL is only a fallback if a page omits scSetupUrl.
 * These deliberately leave the SPA for the app (full page load is
 * correct), so they stay plain <a>.
 */
const SCHOOL_SETUP_URL = 'https://www.saysomethingin.app/schools2';

/* ====================================================================== */

/* Optional `langs` prop: [{label, href}] — renders each language as its
 * own link between the text and the trailing CTA (used on /schools1 to
 * point at the per-language schools pages). */
export function SchoolsCrossLink({ t, langs }) {
  if (!t.scCrossText) return null;
  return (
    <div className="ssi-sc__cross" style={schCross.wrap}>
      <div style={schCross.inner}>
        <span style={schCross.text}>{t.scCrossText}</span>
        {(langs || []).map(l => (
          <Link key={l.href} to={l.href} style={schCross.langLink}>{l.label}</Link>
        ))}
        {t.scCrossCta && <Link to={t.scCrossHref || '#'} style={schCross.link}>{t.scCrossCta} →</Link>}
      </div>
    </div>
  );
}

const schCross = {
  wrap: { background:'var(--pastelRed)', borderBottom:'1px solid var(--border)' },
  inner: { maxWidth:1160, margin:'0 auto', padding:'12px 32px', display:'flex', flexWrap:'wrap', alignItems:'center', justifyContent:'center', gap:'4px 14px', textAlign:'center' },
  text: { fontSize:14, color:'var(--fg-1)' },
  langLink: { fontSize:13, fontWeight:600, color:'var(--brandRed)', textDecoration:'none', background:'#fff', border:'1px solid var(--border)', borderRadius:999, padding:'4px 12px' },
  link: { fontSize:14, fontWeight:700, color:'var(--brandRed)', textDecoration:'none' },
};

/* ====================================================================== */

export function SchoolsHero({ t }) {
  return (
    <section className="ssi-sc__hero" style={schHero.wrap}>
      <div className="ssi-sc__heroInner" style={schHero.inner}>
        <div className="ssi-sc__heroCopy" style={schHero.copy}>
          <div style={schHero.kicker}>{t.scKicker}</div>
          <h1 style={schHero.h1}>{t.scHeadline}</h1>
          <p style={schHero.sub}>{t.scSub}</p>
          <div style={schHero.ctaRow}>
            <a href={t.scSetupUrl || SCHOOL_SETUP_URL} className="btn btn--primary">{t.scCta}</a>
            <a href="#how" style={schHero.ghostLink}>{t.scCta2} →</a>
          </div>
          <p style={schHero.trust}>{t.scTrust}</p>
        </div>
        <div className="ssi-sc__heroImage" style={schHero.image}>
          <div style={schHero.imgFrame}>
            <img src="/assets/teacher-hero.jpg" alt="Teacher and pupils" style={schHero.img}/>
          </div>
          <div className="ssi-sc__heroBadge" style={schHero.badge}>
            <div style={schHero.badgeBig}>{t.scBadgeBig}</div>
            <div style={schHero.badgeSmall}>{t.scBadgeLabel}</div>
          </div>
        </div>
      </div>
    </section>
  );
}

const schHero = {
  wrap: { background:'var(--offWhite)', padding:'56px 32px 72px' },
  inner: { maxWidth:1160, margin:'0 auto', display:'grid', gridTemplateColumns:'1.05fr 1fr', gap:64, alignItems:'center' },
  copy: {},
  kicker: { fontSize:12, letterSpacing:'.16em', textTransform:'uppercase', color:'var(--brandRed)', fontWeight:700, marginBottom:18 },
  h1: { font:'700 64px/1.05 var(--font-display)', margin:'0 0 20px', color:'var(--fg-1)', letterSpacing:'-.01em' },
  sub: { font:'400 20px/1.5 var(--font-body)', color:'var(--fg-2)', margin:'0 0 28px', maxWidth:540 },
  ctaRow: { display:'flex', alignItems:'center', gap:20, marginBottom:18, flexWrap:'wrap' },
  ghostLink: { color:'var(--brandRed)', fontSize:15, fontWeight:600, textDecoration:'none' },
  trust: { fontSize:13, color:'var(--grey)', margin:0 },
  image: { position:'relative' },
  imgFrame: { aspectRatio:'4/5', background:'#e9dccb', borderRadius:20, overflow:'hidden', boxShadow:'0 20px 60px -30px rgba(73,3,0,.35)' },
  img: { width:'100%', height:'100%', objectFit:'cover', display:'block' },
  badge: { position:'absolute', right:-18, bottom:40, background:'#fff', borderRadius:16, padding:'16px 22px', boxShadow:'0 18px 40px -14px rgba(73,3,0,.35)', border:'1px solid var(--border)', minWidth:180 },
  badgeBig: { font:'700 34px/1 var(--font-display)', color:'var(--brandRed)' },
  badgeSmall: { fontSize:12, color:'var(--grey)', marginTop:6, lineHeight:1.4 },
};

/* ---------------------------------------------------------------------- */

/* Prominent Cardiff-study band, placed straight after the hero so the
 * independent research is the first proof point a head/lead sees.
 * Distinct from the smaller research strip inside SchoolsProof – this
 * one is the headline pitch, that one sits with the testimonials.
 * Renders nothing unless the route supplies scRfTitle (so /schools2 is
 * untouched until it opts in). */
export function SchoolsResearchFeature({ t }) {
  if (!t.scRfTitle) return null;
  const stats = [
    { big:t.scRfStat1Big, label:t.scRfStat1Label },
    { big:t.scRfStat2Big, label:t.scRfStat2Label },
    { big:t.scRfStat3Big, label:t.scRfStat3Label },
  ];
  return (
    <section className="ssi-sc__rf" style={schRf.wrap}>
      <div className="ssi-sc__rfInner" style={schRf.inner}>
        <div style={schRf.copy}>
          <div style={schRf.kicker}>{t.scRfKicker}</div>
          <h2 className="ssi-sc__rfH2" style={schRf.h2}>{t.scRfTitle}</h2>
          <p style={schRf.body}>{t.scRfBody}</p>
          {t.scRfHonest && <p style={schRf.honest}>{t.scRfHonest}</p>}
          <a href="https://orca.cardiff.ac.uk/id/eprint/174145/" target="_blank" rel="noopener" style={schRf.link}>{t.scRfLink} →</a>
        </div>
        <figure className="ssi-sc__rfQuote" style={schRf.quoteCard}>
          <blockquote style={schRf.quote}>&ldquo;{t.scRfQuote}&rdquo;</blockquote>
          <figcaption style={schRf.cite}>{t.scRfCite}</figcaption>
        </figure>
      </div>
      <div className="ssi-sc__rfStats" style={schRf.stats}>
        {stats.map((s, i) => (
          <div key={i} style={schRf.stat}>
            <div style={schRf.statBig}>{s.big}</div>
            <div style={schRf.statLabel}>{s.label}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

const schRf = {
  wrap: { background:'var(--brandRed)', padding:'72px 32px' },
  inner: { maxWidth:1160, margin:'0 auto', display:'grid', gridTemplateColumns:'1.15fr 1fr', gap:56, alignItems:'center' },
  copy: {},
  kicker: { fontSize:12, letterSpacing:'.16em', textTransform:'uppercase', color:'var(--gold)', fontWeight:700, marginBottom:14 },
  h2: { font:'700 44px/1.12 var(--font-display)', margin:'0 0 18px', color:'#fff', letterSpacing:'-.01em' },
  body: { font:'400 18px/1.55 var(--font-body)', color:'rgba(255,255,255,.92)', margin:'0 0 22px' },
  honest: { font:'400 16px/1.55 var(--font-body)', color:'rgba(255,255,255,.82)', margin:'-6px 0 22px', paddingLeft:16, borderLeft:'3px solid var(--gold)' },
  link: { display:'inline-block', background:'#fff', color:'var(--brandRed)', fontSize:15, fontWeight:700, textDecoration:'none', padding:'12px 22px', borderRadius:999 },
  quoteCard: { margin:0, background:'rgba(255,255,255,.1)', border:'1px solid rgba(255,255,255,.25)', borderLeft:'4px solid var(--gold)', borderRadius:16, padding:'30px 32px' },
  quote: { font:'400 22px/1.45 var(--font-display)', fontStyle:'italic', margin:'0 0 14px', color:'#fff' },
  cite: { fontSize:13, color:'rgba(255,255,255,.75)' },
  stats: { maxWidth:1160, margin:'52px auto 0', display:'grid', gridTemplateColumns:'repeat(3, 1fr)', gap:20 },
  stat: { background:'rgba(255,255,255,.08)', border:'1px solid rgba(255,255,255,.2)', borderRadius:14, padding:'22px 26px', textAlign:'center' },
  statBig: { font:'700 34px/1.1 var(--font-display)', color:'var(--gold)' },
  statLabel: { fontSize:14, lineHeight:1.45, color:'rgba(255,255,255,.9)', marginTop:8 },
};

/* ---------------------------------------------------------------------- */

export function SchoolsPain({ t }) {
  const lines = [t.scPain1, t.scPain2, t.scPain3, t.scPain4, t.scPain5, t.scPain6];
  return (
    <section className="ssi-sc__pain" style={schPain.wrap}>
      <div style={schPain.inner}>
        <div style={schPain.head}>
          <div style={schPain.kicker}>{t.scPainKicker}</div>
          <h2 style={schPain.h2}>{t.scPainTitle}</h2>
          <p style={schPain.sub}>{t.scPainSub}</p>
        </div>
        <div style={schPain.intro}>{t.scPainIntro}</div>
        <ul className="ssi-sc__painGrid" style={schPain.grid}>
          {lines.map((l, i) => (
            <li key={i} style={schPain.item}>
              <span style={schPain.quoteMark} aria-hidden>“</span>
              <span style={schPain.itemText}>{l}</span>
            </li>
          ))}
        </ul>
        {t.scPainCloser && <p style={schPain.closer}>{t.scPainCloser}</p>}
      </div>
    </section>
  );
}

const schPain = {
  wrap: { background:'#fff', padding:'88px 32px' },
  inner: { maxWidth:1080, margin:'0 auto' },
  head: { textAlign:'center', maxWidth:760, margin:'0 auto 12px' },
  kicker: { fontSize:12, letterSpacing:'.16em', textTransform:'uppercase', color:'var(--brandRed)', fontWeight:700, marginBottom:14 },
  h2: { font:'700 46px/1.1 var(--font-display)', margin:'0 0 16px', color:'var(--fg-1)', letterSpacing:'-.01em' },
  sub: { font:'400 18px/1.55 var(--font-body)', color:'var(--fg-2)', margin:0 },
  intro: { textAlign:'center', fontSize:13, letterSpacing:'.04em', textTransform:'uppercase', color:'var(--grey)', fontWeight:600, margin:'40px 0 24px' },
  grid: { listStyle:'none', margin:0, padding:0, display:'grid', gridTemplateColumns:'repeat(3, 1fr)', gap:16 },
  item: { display:'flex', gap:12, background:'var(--offWhite)', border:'1px solid var(--border)', borderRadius:14, padding:'22px 24px' },
  quoteMark: { font:'700 30px/1 var(--font-display)', color:'var(--brandRed)', opacity:.5 },
  itemText: { fontSize:16, lineHeight:1.5, color:'var(--fg-1)', fontStyle:'italic' },
  closer: { textAlign:'center', font:'400 20px/1.5 var(--font-display)', fontStyle:'italic', color:'var(--fg-1)', margin:'44px auto 0', maxWidth:720 },
};

/* ---------------------------------------------------------------------- */

export function SchoolsAnswer({ t }) {
  const cards = [
    { tag:t.scAnsTag1, title:t.scAnsT1, body:t.scAnsB1 },
    { tag:t.scAnsTag2, title:t.scAnsT2, body:t.scAnsB2 },
    { tag:t.scAnsTag3, title:t.scAnsT3, body:t.scAnsB3 },
  ];
  return (
    <section className="ssi-sc__answer" style={schAns.wrap}>
      <div style={schAns.inner}>
        <div style={schAns.head}>
          <div style={schAns.kicker}>{t.scAnsKicker}</div>
          <h2 style={schAns.h2}>{t.scAnsTitle}</h2>
          <p style={schAns.lede}>{t.scAnsLede}</p>
        </div>
        <div className="ssi-sc__answerGrid" style={schAns.grid}>
          {cards.map((c, i) => (
            <article key={i} style={schAns.card}>
              <div style={schAns.cardTag}>{c.tag}</div>
              <h3 style={schAns.cardH}>{c.title}</h3>
              <p style={schAns.cardB}>{c.body}</p>
            </article>
          ))}
        </div>
        <p style={schAns.closer}>{t.scAnsCloser}</p>
      </div>
    </section>
  );
}

const schAns = {
  wrap: { background:'var(--offWhite)', padding:'88px 32px' },
  inner: { maxWidth:1160, margin:'0 auto' },
  head: { textAlign:'center', maxWidth:760, margin:'0 auto 48px' },
  kicker: { fontSize:12, letterSpacing:'.16em', textTransform:'uppercase', color:'var(--brandRed)', fontWeight:700, marginBottom:14 },
  h2: { font:'700 46px/1.12 var(--font-display)', margin:'0 0 18px', color:'var(--fg-1)', letterSpacing:'-.01em' },
  lede: { font:'400 18px/1.55 var(--font-body)', color:'var(--fg-2)', margin:0 },
  grid: { display:'grid', gridTemplateColumns:'repeat(3, 1fr)', gap:20 },
  card: { background:'#fff', border:'1px solid var(--border)', borderRadius:16, padding:'30px 28px', display:'flex', flexDirection:'column' },
  cardTag: { fontSize:11, letterSpacing:'.12em', textTransform:'uppercase', color:'var(--brandRed)', fontWeight:700, marginBottom:14 },
  cardH: { font:'700 24px/1.25 var(--font-display)', margin:'0 0 12px', color:'var(--fg-1)' },
  cardB: { fontSize:15, lineHeight:1.6, color:'var(--fg-2)', margin:0 },
  closer: { textAlign:'center', font:'400 20px/1.5 var(--font-display)', fontStyle:'italic', color:'var(--fg-1)', margin:'48px auto 0', maxWidth:760 },
};

/* ---------------------------------------------------------------------- */

/* "What actually happens" – the session mechanism, step by step.
 * Renders nothing unless the route supplies scMechTitle. */
export function SchoolsMechanism({ t }) {
  if (!t.scMechTitle) return null;
  const steps = [
    { title:t.scMechStep1T, body:t.scMechStep1B },
    { title:t.scMechStep2T, body:t.scMechStep2B },
    { title:t.scMechStep3T, body:t.scMechStep3B },
    { title:t.scMechStep4T, body:t.scMechStep4B },
  ];
  return (
    <section className="ssi-sc__mech" style={schMech.wrap}>
      <div style={schMech.inner}>
        <div style={schMech.head}>
          <div style={schMech.kicker}>{t.scMechKicker}</div>
          <h2 style={schMech.h2}>{t.scMechTitle}</h2>
          <p style={schMech.lede}>{t.scMechLede}</p>
        </div>
        <ol className="ssi-sc__mechGrid" style={schMech.grid}>
          {steps.map((s, i) => (
            <li key={i} style={schMech.step}>
              <div style={schMech.stepNum}>{i + 1}</div>
              <h3 style={schMech.stepH}>{s.title}</h3>
              <p style={schMech.stepB}>{s.body}</p>
            </li>
          ))}
        </ol>
        <div style={schMech.maths}>{t.scMechMaths}</div>
        <p style={schMech.closer}>{t.scMechCloser}</p>
      </div>
    </section>
  );
}

const schMech = {
  wrap: { background:'#fff', padding:'88px 32px' },
  inner: { maxWidth:1160, margin:'0 auto' },
  head: { textAlign:'center', maxWidth:760, margin:'0 auto 48px' },
  kicker: { fontSize:12, letterSpacing:'.16em', textTransform:'uppercase', color:'var(--brandRed)', fontWeight:700, marginBottom:14 },
  h2: { font:'700 46px/1.1 var(--font-display)', margin:'0 0 16px', color:'var(--fg-1)', letterSpacing:'-.01em' },
  lede: { font:'400 18px/1.55 var(--font-body)', color:'var(--fg-2)', margin:0 },
  grid: { listStyle:'none', margin:0, padding:0, display:'grid', gridTemplateColumns:'repeat(4, 1fr)', gap:16 },
  step: { background:'var(--offWhite)', border:'1px solid var(--border)', borderRadius:16, padding:'26px 24px' },
  stepNum: { width:36, height:36, borderRadius:'50%', background:'var(--brandRed)', color:'#fff', display:'flex', alignItems:'center', justifyContent:'center', font:'700 17px/1 var(--font-display)', marginBottom:16 },
  stepH: { font:'700 21px/1.25 var(--font-display)', margin:'0 0 10px', color:'var(--fg-1)' },
  stepB: { fontSize:15, lineHeight:1.55, color:'var(--fg-2)', margin:0 },
  maths: { background:'var(--pastelRed)', borderRadius:16, padding:'28px 32px', margin:'32px auto 0', maxWidth:920, fontSize:17, lineHeight:1.6, color:'var(--fg-1)' },
  closer: { textAlign:'center', font:'400 21px/1.5 var(--font-display)', fontStyle:'italic', color:'var(--fg-1)', margin:'40px auto 0', maxWidth:720 },
};

/* ---------------------------------------------------------------------- */

/* "Your own Welsh" – teacher-confidence reassurance. Renders nothing
 * unless the route supplies scConfTitle. */
export function SchoolsConfidence({ t }) {
  if (!t.scConfTitle) return null;
  return (
    <section className="ssi-sc__conf" style={schConf.wrap}>
      <div className="ssi-sc__confInner" style={schConf.inner}>
        <div>
          <div style={schConf.kicker}>{t.scConfKicker}</div>
          <h2 style={schConf.h2}>{t.scConfTitle}</h2>
        </div>
        <div>
          <p style={schConf.body}>{t.scConfBody1}</p>
          <p style={schConf.body}>{t.scConfBody2}</p>
          <p style={schConf.closer}>{t.scConfCloser}</p>
        </div>
      </div>
    </section>
  );
}

const schConf = {
  wrap: { background:'var(--offWhite)', padding:'88px 32px' },
  inner: { maxWidth:1080, margin:'0 auto', display:'grid', gridTemplateColumns:'1fr 1.1fr', gap:56, alignItems:'start' },
  kicker: { fontSize:12, letterSpacing:'.16em', textTransform:'uppercase', color:'var(--brandRed)', fontWeight:700, marginBottom:14 },
  h2: { font:'700 40px/1.15 var(--font-display)', margin:0, color:'var(--fg-1)', letterSpacing:'-.01em' },
  body: { font:'400 17px/1.6 var(--font-body)', color:'var(--fg-2)', margin:'0 0 18px' },
  closer: { font:'600 18px/1.5 var(--font-display)', color:'var(--fg-1)', margin:'24px 0 0' },
};

/* ---------------------------------------------------------------------- */

/* `firstFlag` (optional) hoists that language's chip to the front —
 * each page leads with its own language.
 * `plainChips` (optional, string[]) replaces the flag chips entirely
 * with text-only pills — used on /schools/english for the Indian
 * languages of instruction, which have no flag assets. */
export function SchoolsLanguages({ t, firstFlag, plainChips }) {
  const langs = [
    { name:t.scLangWelsh, flag:'wales', ext:'png' },
    { name:t.scLangIrish, flag:'ireland' },
    { name:t.scLangSpanish, flag:'spanish' },
    { name:t.scLangFrench, flag:'french' },
    { name:t.scLangGerman, flag:'german' },
    { name:t.scLangItalian, flag:'italian' },
    { name:t.scLangPortuguese, flag:'portuguese' },
    { name:t.scLangMandarin, flag:'chinese' },
    { name:t.scLangJapanese, flag:'japanese' },
    { name:t.scLangKorean, flag:'korean' },
    { name:t.scLangArabic, flag:'arabic' },
  ].filter(l => l.name)
   .sort((a, b) => (a.flag === firstFlag ? -1 : 0) - (b.flag === firstFlag ? -1 : 0));
  const points = [
    { h:t.scLangPoint1H, b:t.scLangPoint1B },
    { h:t.scLangPoint2H, b:t.scLangPoint2B },
    { h:t.scLangPoint3H, b:t.scLangPoint3B },
  ];
  return (
    <section className="ssi-sc__lang" style={schLang.wrap}>
      <div className="ssi-sc__langInner" style={schLang.inner}>
        <div style={schLang.left}>
          <div style={schLang.kicker}>{t.scLangKicker}</div>
          <h2 style={schLang.h2}>{t.scLangTitle}</h2>
          <p style={schLang.lede}>{t.scLangLede}</p>
          <div style={schLang.chipsLabel}>{t.scLangChipsLabel}</div>
          {plainChips
          ? <div style={schLang.chips}>
              {plainChips.map(name => (
                <span key={name} style={schLang.plainChip}>{name}</span>
              ))}
            </div>
          : <div style={schLang.chips}>
            {langs.map(l => (
              <span key={l.flag} style={schLang.chip}>
                <span style={schLang.chipFlag}>
                  <img src={`/assets/flag-${l.flag}.${l.ext || 'svg'}`} alt="" style={schLang.chipFlagImg}/>
                </span>
                {l.name}
              </span>
            ))}
          </div>}
        </div>
        <div style={schLang.right}>
          {points.map((p, i) => (
            <div key={i} style={schLang.point}>
              <div style={schLang.pointNum}>{String(i+1).padStart(2,'0')}</div>
              <div>
                <div style={schLang.pointH}>{p.h}</div>
                <div style={schLang.pointB}>{p.b}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const schLang = {
  wrap: { background:'#fff', padding:'88px 32px' },
  inner: { maxWidth:1160, margin:'0 auto', display:'grid', gridTemplateColumns:'1.05fr 1fr', gap:64, alignItems:'start' },
  left: {},
  kicker: { fontSize:12, letterSpacing:'.16em', textTransform:'uppercase', color:'var(--brandRed)', fontWeight:700, marginBottom:14 },
  h2: { font:'700 44px/1.12 var(--font-display)', margin:'0 0 16px', color:'var(--fg-1)', letterSpacing:'-.01em' },
  lede: { font:'400 17px/1.6 var(--font-body)', color:'var(--fg-2)', margin:'0 0 28px' },
  chipsLabel: { fontSize:11, letterSpacing:'.14em', textTransform:'uppercase', color:'var(--grey)', fontWeight:600, marginBottom:14 },
  chips: { display:'flex', flexWrap:'wrap', gap:10 },
  chip: { display:'inline-flex', alignItems:'center', gap:8, background:'var(--offWhite)', border:'1px solid var(--border)', borderRadius:999, padding:'6px 14px 6px 6px', fontSize:14, fontWeight:500, color:'var(--fg-1)' },
  chipFlag: { width:24, height:24, borderRadius:'50%', overflow:'hidden', display:'inline-flex', background:'var(--brandRed)', flexShrink:0 },
  chipFlagImg: { width:'100%', height:'100%', objectFit:'cover' },
  plainChip: { display:'inline-flex', alignItems:'center', background:'var(--offWhite)', border:'1px solid var(--border)', borderRadius:999, padding:'8px 16px', fontSize:14, fontWeight:500, color:'var(--fg-1)' },
  right: { display:'flex', flexDirection:'column', gap:18, paddingTop:8 },
  point: { display:'grid', gridTemplateColumns:'auto 1fr', gap:18, background:'var(--offWhite)', border:'1px solid var(--border)', borderRadius:14, padding:'22px 24px' },
  pointNum: { font:'700 22px/1 var(--font-display)', color:'var(--brandRed)', opacity:.7 },
  pointH: { font:'700 18px/1.3 var(--font-display)', margin:'0 0 6px', color:'var(--fg-1)' },
  pointB: { fontSize:15, lineHeight:1.55, color:'var(--fg-2)' },
};

/* ---------------------------------------------------------------------- */

export function SchoolsHow({ t }) {
  return (
    <section id="how" className="ssi-sc__how" style={schHow.wrap}>
      <div style={schHow.inner}>
        <div style={schHow.head}>
          <div style={schHow.kicker}>{t.scHowKicker}</div>
          <h2 style={schHow.h2}>{t.scHowTitle}</h2>
        </div>
        <div className="ssi-sc__howGrid" style={schHow.grid}>
          {[
            { tag:t.scHowATag, title:t.scHowATitle, items:[t.scHowA1, t.scHowA2, t.scHowA3] },
            { tag:t.scHowBTag, title:t.scHowBTitle, items:[t.scHowB1, t.scHowB2, t.scHowB3] },
            { tag:t.scHowCTag, title:t.scHowCTitle, items:[t.scHowC1, t.scHowC2, t.scHowC3] },
          ].map((col, i) => (
            <article key={i} style={schHow.col}>
              <div style={schHow.colTag}>{col.tag}</div>
              <h3 style={schHow.h3}>{col.title}</h3>
              <ul style={schHow.list}>
                {col.items.filter(Boolean).map((it, j) => <li key={j}>{it}</li>)}
              </ul>
            </article>
          ))}
        </div>
        <p style={schHow.pitch}>{t.scHowPitch}</p>
      </div>
    </section>
  );
}

const schHow = {
  wrap: { background:'var(--brandRed)', color:'#fff', padding:'88px 32px' },
  inner: { maxWidth:1160, margin:'0 auto' },
  head: { textAlign:'center', maxWidth:760, margin:'0 auto 56px' },
  kicker: { fontSize:12, letterSpacing:'.16em', textTransform:'uppercase', color:'var(--gold)', fontWeight:700, marginBottom:14 },
  h2: { font:'700 46px/1.1 var(--font-display)', margin:0, letterSpacing:'-.01em', color:'#fff' },
  grid: { display:'grid', gridTemplateColumns:'repeat(3, 1fr)', gap:24 },
  col: { display:'flex', flexDirection:'column', background:'rgba(255,255,255,.08)', borderRadius:16, padding:'28px 26px', border:'1px solid rgba(255,255,255,.16)' },
  colTag: { fontSize:12, letterSpacing:'.12em', textTransform:'uppercase', color:'var(--gold)', fontWeight:700, marginBottom:12 },
  h3: { font:'700 26px/1.2 var(--font-display)', margin:'0 0 18px', color:'#fff' },
  list: { listStyle:'none', padding:0, margin:0, display:'flex', flexDirection:'column', gap:12, fontSize:15, lineHeight:1.55, color:'rgba(255,255,255,.92)' },
  pitch: { textAlign:'center', font:'400 22px/1.5 var(--font-display)', fontStyle:'italic', color:'#fff', margin:'48px auto 0', maxWidth:760 },
};

/* ---------------------------------------------------------------------- */

export function SchoolsBuilt({ t }) {
  // scBuiltImgN (optional) shows a photo instead of the initials circle.
  const people = [
    { name:t.scBuiltName1, role:t.scBuiltRole1, img:t.scBuiltImg1 },
    { name:t.scBuiltName2, role:t.scBuiltRole2, img:t.scBuiltImg2 },
    { name:t.scBuiltName3, role:t.scBuiltRole3, img:t.scBuiltImg3 },
  ].filter(p => p.name).map(p => ({ ...p, initials:(p.name||'').trim()[0] || '' }));
  return (
    <section className="ssi-sc__built" style={schBuilt.wrap}>
      <div className="ssi-sc__builtInner" style={schBuilt.inner}>
        <div style={schBuilt.left}>
          <div style={schBuilt.kicker}>{t.scBuiltKicker}</div>
          <h2 style={schBuilt.h2}>{t.scBuiltTitle}</h2>
          <p style={schBuilt.body}>{t.scBuiltBody}</p>
        </div>
        <div style={schBuilt.people}>
          {people.map((p, i) => (
            <div key={i} style={schBuilt.person}>
              {p.img
                ? <img src={p.img} alt={p.name} style={schBuilt.photo}/>
                : <span style={schBuilt.avatar}>{p.initials}</span>}
              <span>
                <span style={schBuilt.name}>{p.name}</span>
                <span style={schBuilt.role}>{p.role}</span>
              </span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const schBuilt = {
  wrap: { background:'var(--offWhite)', padding:'80px 32px' },
  inner: { maxWidth:1080, margin:'0 auto', display:'grid', gridTemplateColumns:'1.1fr 1fr', gap:56, alignItems:'center' },
  left: {},
  kicker: { fontSize:12, letterSpacing:'.16em', textTransform:'uppercase', color:'var(--brandRed)', fontWeight:700, marginBottom:14 },
  h2: { font:'700 40px/1.15 var(--font-display)', margin:'0 0 16px', color:'var(--fg-1)', letterSpacing:'-.01em' },
  body: { font:'400 17px/1.6 var(--font-body)', color:'var(--fg-2)', margin:0 },
  people: { display:'flex', flexDirection:'column', gap:14 },
  person: { display:'flex', alignItems:'center', gap:16, background:'#fff', border:'1px solid var(--border)', borderRadius:14, padding:'16px 20px' },
  avatar: { width:48, height:48, borderRadius:'50%', background:'var(--brandRed)', color:'#fff', display:'inline-flex', alignItems:'center', justifyContent:'center', font:'700 20px/1 var(--font-display)', flexShrink:0 },
  photo: { width:48, height:48, borderRadius:'50%', objectFit:'cover', objectPosition:'50% 25%', flexShrink:0, display:'block' },
  name: { display:'block', fontSize:16, fontWeight:600, color:'var(--fg-1)' },
  role: { display:'block', fontSize:13, color:'var(--grey)', marginTop:2 },
};

/* ---------------------------------------------------------------------- */

export function SchoolsMethod({ t }) {
  const pillars = [
    { title:t.scMethod1Title, body:t.scMethod1Body },
    { title:t.scMethod2Title, body:t.scMethod2Body },
    { title:t.scMethod3Title, body:t.scMethod3Body },
  ].filter(p => p.title);
  return (
    <section className="ssi-sc__method" style={schMethod.wrap}>
      <div style={schMethod.inner}>
        <div style={schMethod.kicker}>{t.scMethodKicker}</div>
        <h2 style={schMethod.h2}>{t.scMethodTitle}</h2>
        {t.scMethodLede && <p style={schMethod.lede}>{t.scMethodLede}</p>}
        {t.scMethodBody && <p style={schMethod.statement}>{t.scMethodBody}</p>}
        {pillars.length > 0 && <div className="ssi-sc__methodGrid" style={schMethod.threeUp}>
          {pillars.map((p, i) => (
            <div key={i} style={schMethod.pillar}>
              <div style={schMethod.pillarTitle}>{p.title}</div>
              <div style={schMethod.pillarBody}>{p.body}</div>
            </div>
          ))}
        </div>}
        <Link to="/methodology" style={schMethod.more}>{t.scMethodMore} →</Link>
      </div>
    </section>
  );
}

const schMethod = {
  wrap: { background:'#fff', padding:'88px 32px' },
  inner: { maxWidth:1080, margin:'0 auto', textAlign:'center' },
  kicker: { fontSize:12, letterSpacing:'.16em', textTransform:'uppercase', color:'var(--brandRed)', fontWeight:700, marginBottom:14 },
  h2: { font:'700 44px/1.1 var(--font-display)', margin:'0 0 18px', color:'var(--fg-1)', letterSpacing:'-.01em' },
  lede: { font:'400 18px/1.55 var(--font-body)', color:'var(--fg-2)', margin:'0 auto 48px', maxWidth:680 },
  statement: { font:'400 19px/1.6 var(--font-body)', color:'var(--fg-2)', margin:'0 auto 32px', maxWidth:720 },
  threeUp: { display:'grid', gridTemplateColumns:'repeat(3, 1fr)', gap:28, marginBottom:32, textAlign:'left' },
  pillar: { padding:'22px 24px', background:'var(--offWhite)', borderRadius:14, border:'1px solid var(--border)' },
  pillarTitle: { font:'700 18px/1.3 var(--font-display)', marginBottom:10, color:'var(--fg-1)' },
  pillarBody: { fontSize:15, lineHeight:1.55, color:'var(--fg-2)' },
  more: { color:'var(--brandRed)', fontSize:15, fontWeight:600, textDecoration:'none' },
};

/* ---------------------------------------------------------------------- */

export function SchoolsProof({ t }) {
  const tints = { A:'#1e5d5a', B:'var(--brandRed)', C:'var(--gold)' };
  const initialsOf = (name) => (name||'').trim().split(/\s+/).map(w=>w[0]||'').slice(0,2).join('.').toUpperCase();
  const quotes = ['A','B','C'].map(key => ({ key, tint: tints[key] }));
  return (
    <section className="ssi-sc__proof" style={schProof.wrap}>
      <div style={schProof.inner}>
        <div style={schProof.kicker}>{t.scProofKicker}</div>
        <h2 style={schProof.h2}>{t.scProofTitle}</h2>
        {t.scProofBody && <p style={schProof.bodyStatement}>{t.scProofBody}</p>}
        {t.scProofQuoteA && <div className="ssi-sc__proofQuotes" style={{...schProof.quotes, ...(t.scResearchTitle ? {} : {marginBottom:0})}}>
          {quotes.map(q => (
            <figure key={q.key} style={schProof.card}>
              <blockquote style={schProof.q}>{t[`scProofQuote${q.key}`]}</blockquote>
              <figcaption style={schProof.cap}>
                <span style={{...schProof.avatar, background:q.tint, color: q.tint==='var(--gold)' ? 'var(--fg-1)' : '#fff'}}>{initialsOf(t[`scProofName${q.key}`])}</span>
                <span>
                  <span style={schProof.name}>{t[`scProofName${q.key}`]}</span>
                  <span style={schProof.role}>{t[`scProofRole${q.key}`]}</span>
                </span>
              </figcaption>
            </figure>
          ))}
        </div>}
        {t.scResearchTitle && <div className="ssi-sc__research" style={schProof.research}>
          <div style={schProof.researchLeft}>
            <div style={schProof.researchTag}>{t.scResearchKicker}</div>
            <h3 style={schProof.researchH}>{t.scResearchTitle}</h3>
            <p style={schProof.researchBody}>{t.scResearchBody}</p>
            <a href="https://orca.cardiff.ac.uk/id/eprint/174145/" target="_blank" rel="noopener" style={schProof.researchLink}>{t.scResearchLink} →</a>
          </div>
          <div className="ssi-sc__researchRight" style={schProof.researchRight}>
            <blockquote style={schProof.pullQuote}>&ldquo;{t.scResearchPull}&rdquo;</blockquote>
            <div style={schProof.pullCite}>{t.scResearchPullCite}</div>
          </div>
        </div>}
      </div>
    </section>
  );
}

const schProof = {
  wrap: { background:'var(--offWhite)', padding:'88px 32px' },
  inner: { maxWidth:1160, margin:'0 auto' },
  kicker: { fontSize:12, letterSpacing:'.16em', textTransform:'uppercase', color:'var(--brandRed)', fontWeight:700, marginBottom:14, textAlign:'center' },
  h2: { font:'700 44px/1.1 var(--font-display)', margin:'0 0 48px', color:'var(--fg-1)', letterSpacing:'-.01em', textAlign:'center' },
  bodyStatement: { font:'400 21px/1.55 var(--font-display)', color:'var(--fg-1)', margin:'-24px auto 0', maxWidth:760, textAlign:'center' },
  quotes: { display:'grid', gridTemplateColumns:'repeat(3, 1fr)', gap:20, marginBottom:64 },
  card: { margin:0, background:'#fff', borderRadius:16, padding:'28px 26px 24px', border:'1px solid var(--border)' },
  q: { font:'400 17px/1.55 var(--font-display)', margin:'0 0 24px', color:'var(--fg-1)' },
  cap: { display:'flex', alignItems:'center', gap:12 },
  avatar: { width:44, height:44, borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', fontSize:13, fontWeight:700, letterSpacing:'.04em' },
  name: { display:'block', fontSize:14, fontWeight:600, color:'var(--fg-1)' },
  role: { display:'block', fontSize:12, color:'var(--grey)', marginTop:2 },
  research: { background:'#fff', borderRadius:20, padding:'40px 44px', display:'grid', gridTemplateColumns:'1.1fr 1fr', gap:48, alignItems:'center', border:'1px solid var(--border)', boxShadow:'0 10px 30px -20px rgba(73,3,0,.2)' },
  researchLeft: {},
  researchTag: { fontSize:11, letterSpacing:'.14em', textTransform:'uppercase', color:'var(--grey)', fontWeight:700, marginBottom:12 },
  researchH: { font:'700 30px/1.2 var(--font-display)', margin:'0 0 16px', color:'var(--fg-1)' },
  researchBody: { fontSize:16, lineHeight:1.55, color:'var(--fg-2)', margin:'0 0 20px' },
  researchLink: { color:'var(--brandRed)', fontSize:14, fontWeight:600, textDecoration:'none' },
  researchRight: { borderLeft:'3px solid var(--gold)', paddingLeft:28 },
  pullQuote: { font:'400 22px/1.45 var(--font-display)', margin:'0 0 14px', color:'var(--fg-1)', fontStyle:'italic' },
  pullCite: { fontSize:13, color:'var(--grey)' },
};

/* ---------------------------------------------------------------------- */

/* "First in Ireland" – founding-schools invitation (the Irish page's
 * replacement for testimonials). Renders nothing unless the route
 * supplies scFirstTitle. */
export function SchoolsFirst({ t }) {
  if (!t.scFirstTitle) return null;
  return (
    <section className="ssi-sc__first" style={schFirst.wrap}>
      <div style={schFirst.inner}>
        <div style={schFirst.kicker}>{t.scFirstKicker}</div>
        <h2 style={schFirst.h2}>{t.scFirstTitle}</h2>
        <p style={schFirst.body}>{t.scFirstBody1}</p>
        <p style={schFirst.body}>{t.scFirstBody2}</p>
        <div style={schFirst.ctaRow}>
          <Link to="/contact" className="btn btn--primary">{t.scFirstCta}</Link>
        </div>
      </div>
    </section>
  );
}

const schFirst = {
  wrap: { background:'var(--offWhite)', padding:'88px 32px' },
  inner: { maxWidth:760, margin:'0 auto', textAlign:'center' },
  kicker: { fontSize:12, letterSpacing:'.16em', textTransform:'uppercase', color:'var(--brandRed)', fontWeight:700, marginBottom:14 },
  h2: { font:'700 44px/1.1 var(--font-display)', margin:'0 0 24px', color:'var(--fg-1)', letterSpacing:'-.01em' },
  body: { font:'400 17px/1.6 var(--font-body)', color:'var(--fg-2)', margin:'0 0 18px', textAlign:'left' },
  ctaRow: { marginTop:14 },
};

/* ---------------------------------------------------------------------- */

/* "For headteachers, councils & government" – dark policy band. Renders
 * nothing unless the route supplies scGovTitle. */
export function SchoolsGov({ t }) {
  if (!t.scGovTitle) return null;
  return (
    <section className="ssi-sc__gov" style={schGov.wrap}>
      <div style={schGov.inner}>
        <div style={schGov.kicker}>{t.scGovKicker}</div>
        <h2 className="ssi-sc__govH2" style={schGov.h2}>{t.scGovTitle}</h2>
        <div className="ssi-sc__govGrid" style={schGov.grid}>
          <p style={schGov.body}>{t.scGovBody1}</p>
          <p style={schGov.body}>{t.scGovBody2}</p>
        </div>
        {t.scGovBody3 && <p style={schGov.body3}>{t.scGovBody3}</p>}
        <div style={schGov.stat}>{t.scGovStat}</div>
        <div style={schGov.ctaRow}>
          <Link to="/contact" style={schGov.cta}>{t.scGovCta}</Link>
        </div>
      </div>
    </section>
  );
}

const schGov = {
  wrap: { background:'var(--fg-1)', padding:'88px 32px' },
  inner: { maxWidth:1080, margin:'0 auto', textAlign:'center' },
  kicker: { fontSize:12, letterSpacing:'.16em', textTransform:'uppercase', color:'var(--gold)', fontWeight:700, marginBottom:14 },
  h2: { font:'700 42px/1.15 var(--font-display)', margin:'0 auto 40px', color:'#fff', letterSpacing:'-.01em', maxWidth:860 },
  grid: { display:'grid', gridTemplateColumns:'1fr 1fr', gap:32, textAlign:'left' },
  body: { font:'400 17px/1.65 var(--font-body)', color:'rgba(255,255,255,.85)', margin:0 },
  body3: { font:'400 17px/1.65 var(--font-body)', color:'rgba(255,255,255,.85)', margin:'32px 0 0', textAlign:'left' },
  stat: { borderLeft:'4px solid var(--gold)', background:'rgba(255,255,255,.06)', borderRadius:12, padding:'24px 30px', margin:'40px 0 0', font:'400 20px/1.5 var(--font-display)', fontStyle:'italic', color:'#fff', textAlign:'left' },
  ctaRow: { marginTop:36 },
  cta: { display:'inline-block', background:'#fff', color:'var(--fg-1)', fontSize:15, fontWeight:700, textDecoration:'none', padding:'14px 28px', borderRadius:999 },
};

/* ---------------------------------------------------------------------- */

/* `pricingOnly` hides the lesson-player half and centres the pricing
 * card on its own.
 * `courses` (optional, [{code, name}]) overrides the picker list —
 * entries without a `flag` render as text pills. Used on
 * /schools/english for the eight languages of instruction (manifests
 * from Tom, 2026-07-10; no flag assets). */
export function SchoolsSample({ t, pricingOnly, courses }) {
  // Default player picker: Welsh + the 10 paid languages. Per HANDOFF
  // §7, Irish has NO sample course in the player, so it is
  // intentionally absent here (it still appears in the marketing
  // "available now" chips). Course codes match the live
  // <ssi-lesson-player> codes used in components/TeachersOnline.jsx.
  const LANGS = courses || [
    { code:'en-cy-south', name:'Welsh',      flag:'wales', ext:'png' },
    { code:'en-es',       name:'Spanish',    flag:'spanish' },
    { code:'en-fr',       name:'French',     flag:'french' },
    { code:'en-de',       name:'German',     flag:'german' },
    { code:'en-it',       name:'Italian',    flag:'italian' },
    { code:'en-pt',       name:'Portuguese', flag:'portuguese' },
    { code:'en-cmn',      name:'Mandarin',   flag:'chinese' },
    { code:'en-ja',       name:'Japanese',   flag:'japanese' },
    { code:'en-ko',       name:'Korean',     flag:'korean' },
    { code:'en-ar',       name:'Arabic',     flag:'arabic' },
  ];
  const [i, setI] = useState(0);
  const [duration, setDuration] = useState(3);
  const playerRef = useRef(null);

  // Lazy-load the lesson-player script on mount (idempotent across
  // StrictMode double-mount and multiple consumers).
  useEffect(() => {
    ensureLessonPlayer();
  }, []);

  useEffect(() => {
    const el = playerRef.current;
    if (!el) return;
    if (typeof el._pause === 'function' && el._playing) el._pause();
    el.setAttribute('course', LANGS[i].code);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i]);

  useEffect(() => {
    const el = playerRef.current;
    if (el) el.setAttribute('duration', String(duration));
  }, [duration]);

  if (pricingOnly) {
    return (
      <section id="sample" className="ssi-sc__sample" style={schSample.wrap}>
        <div className="ssi-sc__sampleCard" style={{...schSample.card, gridTemplateColumns:'1fr', maxWidth:620}}>
          <div className="ssi-sc__sampleRight" style={schSample.rightSide}>
            <div style={schSample.kicker}>{t.scPricingKicker}</div>
            <h3 style={schSample.sideH}>{t.scPricingTitle}</h3>
            <p style={schSample.sideLede}>{t.scPricingLede}</p>
            <div style={schSample.offer}>
              <div style={schSample.offerBig}>{t.scPricingOfferBig}</div>
              <div style={schSample.offerSub}>{t.scPricingOfferSub}</div>
            </div>
            <ul style={schSample.priceLines}>
              <li style={schSample.priceLine}><span style={schSample.priceLineLabel}>{t.scPricingLine1Label}</span><span style={schSample.priceLineVal}>{t.scPricingLine1Val}</span></li>
              <li style={schSample.priceLine}><span style={schSample.priceLineLabel}>{t.scPricingLine2Label}</span><span style={schSample.priceLineVal}>{t.scPricingLine2Val}</span></li>
            </ul>
            <ul style={schSample.feats}>
              {[t.scPricingFeat1, t.scPricingFeat2, t.scPricingFeat3, t.scPricingFeat4, t.scPricingFeat5].filter(Boolean).map((f, i) => <li key={i}>{f}</li>)}
            </ul>
            <a href={t.scSetupUrl || SCHOOL_SETUP_URL} className="btn btn--primary" style={schSample.cta}>{t.scPricingCta}</a>
            <p style={schSample.note}>{t.scPricingNote}</p>
          </div>
        </div>
      </section>
    );
  }
  return (
    <section id="sample" className="ssi-sc__sample" style={schSample.wrap}>
      <div style={schSample.head}>
        <div style={schSample.kicker}>{t.scSampleKicker}</div>
        <h2 style={schSample.h2}>{t.scSampleTitle}</h2>
        <p style={schSample.lede}>{t.scSampleLede}</p>
      </div>

      <div style={schSample.pickerWrap}>
        <div style={schSample.pickerLabel}>{t.scSamplePickerLabel}</div>
        <div style={schSample.picker}>
          {LANGS.map((l, idx) => {
            const active = idx === i;
            if (!l.flag) {
              return (
                <button key={l.code} type="button" onClick={()=>setI(idx)} style={{...schSample.textChip, borderColor: active ? 'var(--brandRed)' : 'var(--border)', color: active ? 'var(--brandRed)' : 'var(--fg-1)', fontWeight: active ? 600 : 500, boxShadow: active ? '0 6px 16px rgba(178,25,45,.18)' : 'none', transform: active ? 'translateY(-2px)' : 'none'}}>
                  {l.name}
                </button>
              );
            }
            return (
              <button key={l.code} type="button" onClick={()=>setI(idx)} style={{...schSample.chip, transform: active ? 'translateY(-2px)' : 'none'}}>
                <span style={{...schSample.chipFlag, boxShadow: active ? '0 0 0 3px var(--brandRed), 0 6px 16px rgba(178,25,45,.28)' : '0 0 0 1px rgba(30,20,18,.08), 0 1px 2px rgba(30,20,18,.06)'}}>
                  <img src={`/assets/flag-${l.flag}.${l.ext || 'svg'}`} alt="" style={schSample.chipFlagImg}/>
                </span>
                <span style={{...schSample.chipName, color: active ? 'var(--brandRed)' : 'var(--fg-1)', fontWeight: active ? 600 : 500}}>{l.name}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="ssi-sc__sampleCard" style={schSample.card}>
        <div className="ssi-sc__sampleLeft" style={schSample.leftSide}>
          <div style={schSample.playerWrap}>
            <ssi-lesson-player ref={playerRef} course={LANGS[i].code} duration={String(duration)}></ssi-lesson-player>
          </div>
          <div style={schSample.durToggle} role="tablist" aria-label="Demo length">
            {[{ val:3, label:'3 min', sub:'Taster' }, { val:30, label:'30 min', sub:'Full session' }].map(o => {
              const active = duration === o.val;
              return (
                <button key={o.val} role="tab" type="button" aria-selected={active} onClick={()=>setDuration(o.val)} style={{...schSample.durBtn, background: active ? '#fff' : 'transparent', color: active ? 'var(--fg-1)' : 'var(--grey)', boxShadow: active ? '0 1px 2px rgba(30,20,18,.08), 0 4px 10px rgba(30,20,18,.06)' : 'none'}}>
                  <span style={{fontWeight: active ? 600 : 500}}>{o.label}</span>
                  <span style={schSample.durSub}>{o.sub}</span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="ssi-sc__sampleRight" style={schSample.rightSide}>
          <div style={schSample.kicker}>{t.scPricingKicker}</div>
          <h3 style={schSample.sideH}>{t.scPricingTitle}</h3>
          <p style={schSample.sideLede}>{t.scPricingLede}</p>

          <div style={schSample.offer}>
            <div style={schSample.offerBig}>{t.scPricingOfferBig}</div>
            <div style={schSample.offerSub}>{t.scPricingOfferSub}</div>
          </div>

          <ul style={schSample.priceLines}>
            <li style={schSample.priceLine}><span style={schSample.priceLineLabel}>{t.scPricingLine1Label}</span><span style={schSample.priceLineVal}>{t.scPricingLine1Val}</span></li>
            <li style={schSample.priceLine}><span style={schSample.priceLineLabel}>{t.scPricingLine2Label}</span><span style={schSample.priceLineVal}>{t.scPricingLine2Val}</span></li>
          </ul>

          <ul style={schSample.feats}>
            {[t.scPricingFeat1, t.scPricingFeat2, t.scPricingFeat3, t.scPricingFeat4, t.scPricingFeat5].filter(Boolean).map((f, i) => <li key={i}>{f}</li>)}
          </ul>

          <a href={t.scSetupUrl || SCHOOL_SETUP_URL} className="btn btn--primary" style={schSample.cta}>{t.scPricingCta}</a>
          <p style={schSample.note}>{t.scPricingNote}</p>
        </div>
      </div>
    </section>
  );
}

const schSample = {
  wrap: { background:'#fff', padding:'88px 32px' },
  head: { maxWidth:720, margin:'0 auto 40px', textAlign:'center' },
  kicker: { fontSize:12, letterSpacing:'.16em', textTransform:'uppercase', color:'var(--brandRed)', fontWeight:700, marginBottom:12 },
  h2: { font:'700 44px/1.1 var(--font-display)', margin:'0 0 14px', color:'var(--fg-1)', letterSpacing:'-.01em' },
  lede: { font:'400 17px/1.55 var(--font-body)', color:'var(--fg-2)', margin:0, textWrap:'pretty' },
  pickerWrap: { maxWidth:1160, margin:'0 auto 40px', textAlign:'center' },
  pickerLabel: { fontSize:11, letterSpacing:'.16em', textTransform:'uppercase', color:'var(--grey)', fontWeight:600, marginBottom:20 },
  picker: { display:'flex', flexWrap:'wrap', justifyContent:'center', gap:'18px 20px', marginTop:4 },
  chip: { background:'transparent', border:'none', padding:0, display:'flex', flexDirection:'column', alignItems:'center', gap:6, fontFamily:'inherit', cursor:'pointer', transition:'transform 160ms ease', minWidth:64 },
  textChip: { background:'#fff', border:'2px solid var(--border)', borderRadius:999, padding:'10px 20px', fontFamily:'inherit', fontSize:15, cursor:'pointer', transition:'transform 160ms ease, border-color 160ms ease, color 160ms ease, box-shadow 160ms ease' },
  chipFlag: { width:48, height:48, borderRadius:'50%', overflow:'hidden', display:'flex', alignItems:'center', justifyContent:'center', background:'var(--brandRed)', transition:'box-shadow 160ms ease' },
  chipFlagImg: { width:'100%', height:'100%', objectFit:'cover' },
  chipName: { fontSize:12, transition:'color 160ms ease' },
  card: { maxWidth:1160, margin:'0 auto', display:'grid', gridTemplateColumns:'1fr 1fr', background:'#fff', borderRadius:24, overflow:'hidden', border:'1px solid var(--border)', boxShadow:'0 1px 2px rgba(0,0,0,.04), 0 20px 50px -25px rgba(73,3,0,.2)' },
  leftSide: { padding:'44px 40px 40px', background:'linear-gradient(180deg, #fff 0%, #faf7f2 100%)', borderRight:'1px solid var(--border)', display:'flex', flexDirection:'column', justifyContent:'center', alignItems:'center' },
  rightSide: { padding:'44px 40px 40px', display:'flex', flexDirection:'column' },
  sideH: { font:'700 30px/1.2 var(--font-display)', margin:'0 0 12px', color:'var(--fg-1)', letterSpacing:'-.01em' },
  sideLede: { font:'400 15px/1.55 var(--font-body)', color:'var(--fg-2)', margin:'0 0 24px' },
  playerWrap: { width:'100%', display:'flex', justifyContent:'center', marginTop:8 },
  durToggle: { display:'inline-flex', padding:4, background:'rgba(30,20,18,.05)', borderRadius:999, gap:2, marginTop:20, alignSelf:'center' },
  durBtn: { border:0, padding:'8px 16px', borderRadius:999, cursor:'pointer', fontFamily:'inherit', fontSize:13, transition:'background 160ms ease, color 160ms ease, box-shadow 160ms ease', display:'inline-flex', alignItems:'baseline', gap:6 },
  durSub: { fontSize:11, color:'var(--grey)', fontWeight:500 },
  paths: { display:'flex', flexDirection:'column', gap:12, marginBottom:24 },
  offer: { background:'var(--brandRed)', color:'#fff', borderRadius:14, padding:'18px 20px', marginBottom:18 },
  offerBig: { font:'700 28px/1.1 var(--font-display)', letterSpacing:'-.01em' },
  offerSub: { fontSize:14, opacity:.92, marginTop:4 },
  priceLines: { listStyle:'none', margin:'0 0 18px', padding:0, display:'flex', flexDirection:'column', gap:0 },
  priceLine: { display:'flex', justifyContent:'space-between', alignItems:'baseline', gap:16, padding:'12px 0', borderBottom:'1px solid var(--border)', fontSize:15 },
  priceLineLabel: { color:'var(--fg-2)' },
  priceLineVal: { fontWeight:700, color:'var(--fg-1)' },
  feats: { listStyle:'none', margin:'0 0 24px', padding:0, display:'flex', flexDirection:'column', gap:8, fontSize:14, lineHeight:1.5, color:'var(--fg-2)' },
  path: { background:'var(--offWhite)', border:'1px solid var(--border)', borderRadius:14, padding:'18px 20px' },
  pathTag: { fontSize:11, letterSpacing:'.1em', textTransform:'uppercase', color:'var(--brandRed)', fontWeight:700, marginBottom:6 },
  pathBody: { fontSize:14, lineHeight:1.5, color:'var(--fg-2)', marginBottom:10 },
  pathLink: { fontSize:14, fontWeight:600, color:'var(--brandRed)', textDecoration:'none' },
  cta: { display:'inline-flex', alignItems:'center', justifyContent:'center', width:'100%' },
  note: { fontSize:12, color:'var(--grey)', margin:'12px 0 0', textAlign:'center', lineHeight:1.5 },
};

/* ---------------------------------------------------------------------- */

export function SchoolsFAQ({ t }) {
  const items = [
    { q:t.scFaqQ1, a:t.scFaqA1 }, { q:t.scFaqQ2, a:t.scFaqA2 },
    { q:t.scFaqQ3, a:t.scFaqA3 }, { q:t.scFaqQ4, a:t.scFaqA4 },
    { q:t.scFaqQ5, a:t.scFaqA5 }, { q:t.scFaqQ6, a:t.scFaqA6 },
    { q:t.scFaqQ7, a:t.scFaqA7 }, { q:t.scFaqQ8, a:t.scFaqA8 },
    { q:t.scFaqQ9, a:t.scFaqA9 }, { q:t.scFaqQ10, a:t.scFaqA10 },
  ].filter(it => it.q);
  const [open, setOpen] = useState(0);
  const toggle = (idx) => setOpen(open === idx ? -1 : idx);
  return (
    <section className="ssi-sc__faq" style={schFaq.wrap}>
      <div className="ssi-sc__faqInner" style={schFaq.inner}>
        <aside className="ssi-sc__faqSide" style={schFaq.side}>
          <div style={schFaq.kicker}>{t.scFaqKicker}</div>
          <h2 style={schFaq.h2}>{t.scFaqTitle}</h2>
          <p style={schFaq.lede}>{t.scFaqLede}</p>
          <div style={schFaq.contact}>
            <div style={schFaq.contactLabel}>Still unsure?</div>
            <Link to="/contact" style={schFaq.contactLink}>{t.scFaqContact}</Link>
          </div>
        </aside>
        <ol style={schFaq.list}>
          {items.map((it, idx) => {
            const isOpen = open === idx;
            return (
              <li key={idx} style={{...schFaq.item, borderColor: isOpen ? 'var(--brandRed)' : 'var(--border)', background: isOpen ? '#fff' : 'transparent', boxShadow: isOpen ? '0 8px 24px rgba(178,25,45,.08)' : 'none'}}>
                <button type="button" onClick={()=>toggle(idx)} aria-expanded={isOpen} style={schFaq.qBtn}>
                  <span style={schFaq.qNum}>{String(idx+1).padStart(2,'0')}</span>
                  <span style={schFaq.qText}>{it.q}</span>
                  <span style={{...schFaq.qIcon, transform: isOpen ? 'rotate(45deg)' : 'rotate(0deg)', color: isOpen ? 'var(--brandRed)' : 'var(--grey)'}} aria-hidden>+</span>
                </button>
                <div style={{...schFaq.aWrap, maxHeight: isOpen ? 500 : 0, opacity: isOpen ? 1 : 0}}>
                  <p style={schFaq.aText}>{it.a}</p>
                </div>
              </li>
            );
          })}
        </ol>
      </div>
    </section>
  );
}

const schFaq = {
  wrap: { background:'var(--offWhite)', padding:'104px 32px 96px' },
  inner: { maxWidth:1200, margin:'0 auto', display:'grid', gridTemplateColumns:'340px 1fr', gap:64, alignItems:'start' },
  side: { position:'sticky', top:40 },
  kicker: { fontSize:12, letterSpacing:'.14em', textTransform:'uppercase', color:'var(--brandRed)', fontWeight:600, marginBottom:12 },
  h2: { fontFamily:'var(--font-display)', fontSize:44, lineHeight:1.05, margin:'0 0 14px', letterSpacing:'-.015em' },
  lede: { fontSize:16, color:'var(--grey)', lineHeight:1.6, margin:'0 0 28px', textWrap:'pretty' },
  contact: { borderTop:'1px solid var(--border)', paddingTop:20 },
  contactLabel: { fontSize:13, color:'var(--grey)', marginBottom:6 },
  contactLink: { fontSize:16, color:'var(--brandRed)', fontWeight:600, textDecoration:'none' },
  list: { listStyle:'none', margin:0, padding:0, display:'flex', flexDirection:'column', gap:12 },
  item: { border:'1px solid var(--border)', borderRadius:14, transition:'border-color 160ms ease, background 160ms ease, box-shadow 160ms ease', overflow:'hidden' },
  qBtn: { display:'grid', gridTemplateColumns:'auto 1fr auto', alignItems:'center', gap:20, width:'100%', padding:'22px 26px', background:'transparent', border:0, cursor:'pointer', textAlign:'left', font:'inherit', color:'inherit' },
  qNum: { fontFamily:'var(--font-display)', fontSize:14, color:'var(--brandRed)', opacity:.7, letterSpacing:'.05em' },
  qText: { fontFamily:'var(--font-display)', fontSize:20, lineHeight:1.3, color:'var(--fg-1)', letterSpacing:'-.005em', textWrap:'balance' },
  qIcon: { fontSize:26, fontWeight:300, width:28, textAlign:'center', transition:'transform 200ms ease, color 160ms ease' },
  aWrap: { overflow:'hidden', transition:'max-height 260ms ease, opacity 200ms ease' },
  aText: { margin:0, padding:'0 26px 26px 82px', fontSize:16, lineHeight:1.65, color:'var(--grey)', textWrap:'pretty' },
};

/* ---------------------------------------------------------------------- */

export function SchoolsFooterCTA({ t }) {
  return (
    <section className="ssi-sc__footerCta" style={schCta.wrap}>
      <div style={schCta.inner}>
        <h2 style={schCta.h2}>{t.scFooterCtaTitle}</h2>
        <p style={schCta.sub}>{t.scFooterCtaSub}</p>
        <div style={schCta.btnRow}>
          <a href={t.scSetupUrl || SCHOOL_SETUP_URL} style={schCta.primaryBtn}>{t.scFooterCtaBtn}</a>
          <Link to="/contact" style={schCta.ghostBtn}>{t.scFooterCtaBtn2}</Link>
        </div>
        <p style={schCta.note}>{t.scFooterCtaNote}</p>
      </div>
    </section>
  );
}

const schCta = {
  wrap: { background:'var(--brandRed)', color:'#fff', padding:'96px 32px', textAlign:'center' },
  inner: { maxWidth:680, margin:'0 auto' },
  h2: { font:'700 52px/1.1 var(--font-display)', margin:'0 0 20px', letterSpacing:'-.01em', color:'#fff' },
  sub: { font:'400 19px/1.55 var(--font-body)', margin:'0 0 36px', opacity:.92, color:'#fff' },
  btnRow: { display:'flex', gap:16, justifyContent:'center', flexWrap:'wrap' },
  primaryBtn: { display:'inline-flex', alignItems:'center', justifyContent:'center', padding:'14px 28px', borderRadius:30, background:'#fff', color:'var(--brandRed)', fontSize:16, fontWeight:600, textDecoration:'none' },
  ghostBtn: { display:'inline-flex', alignItems:'center', justifyContent:'center', padding:'14px 28px', borderRadius:30, background:'transparent', color:'#fff', border:'1px solid rgba(255,255,255,.6)', fontSize:16, fontWeight:600, textDecoration:'none' },
  note: { fontSize:13, color:'rgba(255,255,255,.85)', margin:'24px 0 0', lineHeight:1.5 },
};
